import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import NoDataMessage from './NoDataMessage';
import NumberWithBar from '~/components/patterns/charts/structures/NumberWithBar';
import WebVitalFormatter from '~/components/app/WebVitalFormatter';
import WebVitalDistributionBarChart from '~/components/app/WebVitalDistributionBarChart';

import WidgetCell from '~/components/patterns/widgets/WidgetCell';
import Widget from './Widget';

import {
	useInteractionToNextPaintWidgetQuery,
} from './InteractionToNextPaintWidget.gql';

import useDashboardDateRange from '~/hooks/useDashboardDateRange';
import useDashboardQuery from '~/hooks/useDashboardQuery';
import useDashboardScopeSection from '~/hooks/useDashboardScopeSection';
import useWebsiteId from '~/hooks/useWebsiteId';

import {
	WidgetType,
	formatDateForQuery,
} from '~/model/dashboard';

import {
	WebVital,
} from '~/model/webVitals';



const messages = defineMessages({
	primaryActionLink: {
		id: 'ui.dashboardInteractionToNextPaintWidget.primaryActionLink',
	},
	title: {
		id: 'ui.dashboardInteractionToNextPaintWidget.title',
	},
	subtitle: {
		id: 'ui.dashboardInteractionToNextPaintWidget.subtitle',
	},
});



const InteractionToNextPaintWidget: React.FC = () => {
	const dateRange = useDashboardDateRange();
	const websiteId = useWebsiteId();
	const scopeSection = useDashboardScopeSection();

	const {
		data: metricData,
		loading,
		reasonForNoData,
	} = useDashboardQuery('webVitalsOriginSummaryData', useInteractionToNextPaintWidgetQuery, {
		variables: {
			scope: scopeSection.scope,
			websiteId,
			...formatDateForQuery(dateRange),
		},
	});

	return (
		<WidgetCell type={WidgetType.InteractionToNextPaint}>
			<Widget
				flag={(
					<FormattedMessage {...messages.subtitle} />
				)}
				label={(
					<FormattedMessage {...messages.title} />
				)}
				loading={loading}
				overlay={!loading && !metricData?.interactionToNextPaint && (
					<NoDataMessage reasonForNoData={reasonForNoData} />
				)}
				primaryActionLink={{
					label: (
						<FormattedMessage {...messages.primaryActionLink} />
					),
					routeName: 'website.platform',
					routeParams: {
						websiteId,
					},
				}}
				showEndDate={true}
			>
				{!loading && metricData?.interactionToNextPaint && (
					<NumberWithBar
						barChart={(
							<WebVitalDistributionBarChart
								maximumFast={metricData.interactionToNextPaint.maximumFast}
								maximumGood={metricData.interactionToNextPaint.maximumGood}
								percentageFast={metricData.interactionToNextPaint.percentageFast}
								percentageGood={metricData.interactionToNextPaint.percentageGood}
								percentageSlow={metricData.interactionToNextPaint.percentageSlow}
								type={WebVital.INP}
							/>
						)}
						number={(
							<WebVitalFormatter
								showStatusIndicator={true}
								type={WebVital.INP}
								value={metricData.interactionToNextPaint.percentile}
								verdict={metricData.interactionToNextPaint.verdict}
							/>
						)}
						stretchedBarChart={true}
					/>
				)}
			</Widget>
		</WidgetCell>
	);
};



export default InteractionToNextPaintWidget;
